import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getPreferredPageSize,
  rememberPageSize,
  type RecordListPageSize,
} from "../pagination/pageSizePreference";

export type ClientListRecord = Record<string, unknown> & { id?: string };
export type ClientListSortDirection = "asc" | "desc";

function updateParams(
  current: URLSearchParams,
  values: Record<string, string | undefined>,
) {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(values))
    value ? next.set(key, value) : next.delete(key);
  return next;
}

function text(record: ClientListRecord) {
  return [
    record.code,
    record.number,
    record.name,
    record.description,
    record.subject,
    record.customerReference,
    record.supplierReference,
    record.status,
  ]
    .filter((value) => value !== null && value !== undefined)
    .join(" ")
    .toLocaleLowerCase();
}

function status(record: ClientListRecord) {
  return String(
    record.status ??
      (record.isActive === false
        ? "Inactive"
        : record.isActive === true
          ? "Active"
          : ""),
  );
}

export function useClientRecordList<T extends ClientListRecord>(
  records: readonly T[],
  identity: string,
) {
  const [params, setParams] = useSearchParams();
  const appliedQuery = params.get("q") ?? "";
  const appliedStatus = params.get("status") ?? "";
  const [query, setQuery] = useState(appliedQuery);
  const [statusFilter, setStatusFilter] = useState(appliedStatus);
  const pageSizeParam = Number(params.get("pageSize"));
  const [pageSize, setPageSize] = useState<RecordListPageSize>(
    ([10, 25, 50, 100] as number[]).includes(pageSizeParam)
      ? (pageSizeParam as RecordListPageSize)
      : getPreferredPageSize(),
  );
  const page = Math.max(1, Number(params.get("page")) || 1);
  const sort = params.get("sort") ?? "code";
  const direction: ClientListSortDirection =
    params.get("direction") === "desc" ? "desc" : "asc";

  useEffect(() => {
    setQuery(appliedQuery);
    setStatusFilter(appliedStatus);
  }, [appliedQuery, appliedStatus, identity]);

  const statusOptions = useMemo(
    () => [...new Set(records.map(status).filter(Boolean))].sort(),
    [records],
  );
  const filtered = useMemo(
    () =>
      records.filter(
        (record) =>
          (!appliedQuery ||
            text(record).includes(appliedQuery.toLocaleLowerCase())) &&
          (!appliedStatus || status(record) === appliedStatus),
      ),
    [records, appliedQuery, appliedStatus],
  );
  const ordered = useMemo(
    () =>
      [...filtered].sort((left, right) => {
        const a = left[sort] ?? left.code ?? left.number ?? left.name ?? "";
        const b = right[sort] ?? right.code ?? right.number ?? right.name ?? "";
        const comparison =
          typeof a === "number" && typeof b === "number"
            ? a - b
            : String(a).localeCompare(String(b), undefined, {
                numeric: true,
                sensitivity: "base",
              });
        return direction === "asc" ? comparison : -comparison;
      }),
    [filtered, sort, direction],
  );
  const totalCount = ordered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(page, totalPages);
  const rows = ordered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const set = (values: Record<string, string | undefined>) =>
    setParams(updateParams(params, values));
  return {
    rows,
    totalCount,
    page: safePage,
    pageSize,
    query,
    setQuery,
    statusFilter,
    setStatusFilter,
    statusOptions,
    sort,
    direction,
    apply: () =>
      set({
        q: query.trim() || undefined,
        status: statusFilter || undefined,
        page: "1",
        pageSize: String(pageSize),
      }),
    clear: () => {
      setQuery("");
      setStatusFilter("");
      set({
        q: undefined,
        status: undefined,
        page: "1",
        pageSize: String(pageSize),
      });
    },
    changePage: (next: number) => set({ page: String(next) }),
    changePageSize: (next: RecordListPageSize) => {
      setPageSize(next);
      rememberPageSize(next);
      set({ page: "1", pageSize: String(next) });
    },
    changeSort: (field: string) =>
      set({
        sort: field,
        direction: sort === field && direction === "asc" ? "desc" : "asc",
        page: "1",
      }),
  };
}
